import { GetPartnerBusinessProfileResponse } from '@/api/type/types';
import { MultiLineText } from '@/components/text/MultiLineText';
import { useLoading } from '@/context/LoadingContext';
import { useAlert } from '@/hooks/useAlert';
import {
  FileDeleteSource,
  FileTransferService,
  FileTransferSource,
  FileUploadSource,
} from '@/libs/r2-service-client';
import { useAppSelector } from '@/stores/hooks';
import { businessProfile } from '@/stores/slices/business-profile/businessProfileSlice';
import { fonts } from '@/theme/fonts';
import { formatAttachedFile } from '@/utils/file/formatAttachedFile';
import { getFileUrl } from '@/utils/file/getFileUrl';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import ProfileTitle from '../../component/ProfileTitle';

interface BusinessRepresentativeImageProps {
  isEdit: boolean;
  profile: GetPartnerBusinessProfileResponse;
}

const BusinessRepresentativeImage = ({
  isEdit,
  profile,
}: BusinessRepresentativeImageProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showAlert } = useAlert();
  const { putBusinessProfile } = useAppSelector(
    (state) => state.businessProfile
  );
  const { startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const onClickUploadBox = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      startLoading();
      const MAX_TOTAL_SIZE = 1 * 1024 * 1024 * 1024; // 1GB
      const allowExtensions = ['jpeg', 'jpg', 'png', 'bmp', 'tif', 'tiff'];

      try {
        // 확장자 체크
        const invalidFiles = files.filter((file) => {
          const ext = file.name.split('.').pop()?.toLowerCase();
          return !ext || !allowExtensions.includes(ext);
        });
        if (invalidFiles.length) {
          showAlert({
            message: t('onlyFilesAreSupported', {
              type: 'JPEG/JPG, PNG, Bmp, TIFF',
            }),
          });
          return;
        }
        // 용량 체크
        const totalSize = files.reduce((acc, file) => acc + file.size, 0);
        if (totalSize > MAX_TOTAL_SIZE) {
          showAlert({
            message: t('fileUploadLimitfileSizeMustBeMbOrLess', {
              size: '1GB',
            }),
          });
          return;
        }
        const uploadSource = new FileUploadSource('USER_UPLOAD');
        files.forEach((file) => uploadSource.addBrowserData(file));

        const transferSource = new FileTransferSource(uploadSource);
        const fileService = new FileTransferService();

        const uploadResult = await fileService.transfer(transferSource);
        if (!uploadResult.upload) return;
        const existingFiles = putBusinessProfile?.representationImages || [];
        const newFiles = formatAttachedFile(uploadResult.upload);
        const mergedIds = Array.from(new Set([...existingFiles, ...newFiles]));

        dispatch(
          businessProfile.putBusinessProfile({
            representationImages: mergedIds,
          })
        );

        if (fileInputRef.current) fileInputRef.current.value = '';
      } finally {
        stopLoading();
      }
    },
    [
      dispatch,
      putBusinessProfile?.representationImages,
      showAlert,
      startLoading,
      stopLoading,
      t,
    ]
  );

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0)
        if (e.target.files) {
          const newFiles = Array.from(files);
          const originLength = putBusinessProfile?.representationImages
            ? putBusinessProfile?.representationImages.length
            : 0;
          if (originLength + newFiles.length > 5) {
            showAlert({
              message: t('youveExceededTheImageUploadLimit'),
            });
            e.target.value;
            return;
          }
          uploadFiles(Array.from(e.target.files));
        }
    },
    [putBusinessProfile?.representationImages, showAlert, t, uploadFiles]
  );

  const onDeleteFile = useCallback(
    (fileId: string) => async () => {
      if (fileId === '') return;
      startLoading();

      try {
        const deleteSource = new FileDeleteSource();
        deleteSource.add(fileId);
        const transferSource = new FileTransferSource(null, deleteSource);
        const fileService = new FileTransferService();

        const deleteResult = await fileService.transfer(transferSource);

        if (!deleteResult.delete) return;

        const remainingFiles = (
          putBusinessProfile?.representationImages || []
        ).filter((f) => !deleteResult.delete!.some((d) => d.fileId === f.id));
        dispatch(
          businessProfile.putBusinessProfile({
            representationImages: remainingFiles,
          })
        );
      } finally {
        stopLoading();
      }
    },
    [
      dispatch,
      putBusinessProfile?.representationImages,
      startLoading,
      stopLoading,
    ]
  );

  const displayImages = useMemo(() => {
    if (isEdit) {
      return putBusinessProfile?.representationImages ?? [];
    }
    return profile.representationImages ?? [];
  }, [
    isEdit,
    profile.representationImages,
    putBusinessProfile?.representationImages,
  ]);

  return (
    <Container>
      <Row>
        <ProfileTitle title={t('representativeImages')} />
        <ImgSection>
          <UploadWrap>
            <UploadButton
              data-edit={isEdit}
              onClick={onClickUploadBox}
              disabled={!isEdit}
            >
              +
            </UploadButton>
            <FileInput
              type="file"
              ref={fileInputRef}
              onChange={onFileChange}
              multiple
            />
            <UploadDesc>
              {t('pleaseUploadAnImageThatRepresentsYourCenter')}
              <br />
              {t('youCanUploadUpTo5Images')}
              <br />
              {t('fileFormatsJpegJpgPngBmpTiffSize1gb')}
            </UploadDesc>
          </UploadWrap>
        </ImgSection>
      </Row>
      {displayImages.length > 0 && (
        <ImgContainer>
          {displayImages.map((item) => (
            <ImgWrap key={item.id}>
              <RepresenativeImgs $imgUrl={getFileUrl(item.id)} />
              {isEdit && (
                <DeleteButton onClick={onDeleteFile(item.id)}>x</DeleteButton>
              )}
            </ImgWrap>
          ))}
        </ImgContainer>
      )}
    </Container>
  );
};

export default BusinessRepresentativeImage;

const Row = styled.div`
  display: flex;
  gap: 18px;
`;

const Label = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 20px;
  line-height: 140%;
  width: 147px;
  flex-shrink: 0;
  color: #6e98f8;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  &[data-en='true'] {
    ${Row} {
      gap: 0;
    }
    ${Label} {
      width: 165px;
    }
  }
`;
const UploadWrap = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  align-items: center;
`;

const UploadButton = styled.button`
  font-size: 15px;
  line-height: 140%;
  color: #000000;
  border: 1px solid #ababab;
  border-radius: 9px;
  height: 40px;
  width: 65px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: auto;
  &[data-edit='true'] {
    cursor: pointer;
    border: 1px solid #c285ff;
    color: #c285ff;
  }
`;

const UploadDesc = styled.div`
  font-size: 12px;
  line-height: 120%;
  letter-spacing: -3%;
  color: #312e4b;
  text-align: start;
`;

const ImgSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 39px;
`;

const ImgContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
`;

const ImgWrap = styled.div`
  position: relative;
`;
interface RepresenativeImgsProps {
  $imgUrl: string;
}

const RepresenativeImgs = styled.div<RepresenativeImgsProps>`
  width: 200px;
  height: 150px;
  background-image: url(${({ $imgUrl }) => $imgUrl});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 20px;
  border: 2px solid #d9d9d9;
`;

const DeleteButton = styled.button`
  font-size: 24px;
  color: #d9d9d9;
  position: absolute;
  top: 0;
  right: 9px;
`;

const FileInput = styled.input`
  display: none;
`;
